import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Scissors, Upload, Layers, ExternalLink } from 'lucide-react';
import api from '../../lib/api';
import Button from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import type { Submission } from '../../types';
import type { AiVideoEditJobRow } from './CreativeCuts';
import { toSlug } from '../../lib/slug';
import { formatDescriptAgentResponseForDisplay } from '../../lib/formatDescriptAgentResponse';

const PRESET_OPTIONS = [6, 15, 30] as const;

type OutputRow = {
  job_id?: string;
  label?: string;
  download_url?: string;
  share_url?: string;
  download_url_expires_at?: string;
  ok?: boolean;
};

export default function CreativeCutsNewPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const jobIdParam = searchParams.get('jobId');
  const submissionIdParam = searchParams.get('submissionId');
  const mediaIdParam = searchParams.get('mediaId');
  const sourceUpload = searchParams.get('source') === 'upload';
  const bulkUpload = searchParams.get('bulk') === '1';

  const [submission, setSubmission] = useState<Submission | null>(null);
  const [job, setJob] = useState<AiVideoEditJobRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedPresets, setSelectedPresets] = useState<Set<number>>(
    () => new Set([6, 15, 30])
  );
  const [extraPrompt, setExtraPrompt] = useState('');
  const [projectName, setProjectName] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  const [bulkFiles, setBulkFiles] = useState<File[]>([]);
  const [bulkRows, setBulkRows] = useState<
    { projectName: string; extraPrompt: string; presets: Set<number> }[]
  >([]);
  const [batchLabel, setBatchLabel] = useState('');
  const [sharedDirection, setSharedDirection] = useState('');
  const [sharedPresets, setSharedPresets] = useState<Set<number>>(() => new Set([6, 15, 30]));
  const [showOverrides, setShowOverrides] = useState(false);

  const [followUpText, setFollowUpText] = useState('');
  const [followUpSending, setFollowUpSending] = useState(false);

  const selectedVideoId = mediaIdParam || '';

  useEffect(() => {
    if (sourceUpload || jobIdParam || !submissionIdParam) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<Submission>(`/submissions/${submissionIdParam}`);
        if (!cancelled) setSubmission(res.data);
      } catch {
        if (!cancelled) setError('Could not load submission.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [submissionIdParam, sourceUpload, jobIdParam]);

  useEffect(() => {
    if (sourceUpload || !submission?.media || !submissionIdParam) return;
    const videos = submission.media.filter((m) => m.type === 'VIDEO');
    if (videos.length === 0) return;
    if (!mediaIdParam || !videos.some((v) => v.id === mediaIdParam)) {
      const first = videos[0].id;
      const sid = submissionIdParam ?? '';
      navigate(
        `/agency/ai-video-edits/new?submissionId=${encodeURIComponent(sid)}&mediaId=${encodeURIComponent(first)}`,
        { replace: true }
      );
    }
  }, [submission, submissionIdParam, mediaIdParam, navigate, sourceUpload]);

  useEffect(() => {
    if (!jobIdParam) return;
    let cancelled = false;

    const load = async () => {
      try {
        const res = await api.get<AiVideoEditJobRow>(`/ai-video-edit-jobs/${jobIdParam}`);
        if (!cancelled) {
          setJob(res.data);
          setError(null);
        }
      } catch (e: any) {
        const msg = e?.response?.data?.error;
        if (!cancelled) setError(typeof msg === 'string' ? msg : 'Could not load job.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    setLoading(true);
    void load();
    const timer = setInterval(load, 4000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [jobIdParam]);

  const videoOptions = useMemo(() => {
    if (!submission?.media) return [];
    return submission.media.filter((m) => m.type === 'VIDEO');
  }, [submission]);

  const togglePreset = (sec: number) => {
    setSelectedPresets((prev) => {
      const next = new Set(prev);
      if (next.has(sec)) next.delete(sec);
      else next.add(sec);
      return next;
    });
  };

  const toggleBulkPreset = (rowIndex: number, sec: number) => {
    setBulkRows((rows) => {
      const next = [...rows];
      const row = next[rowIndex];
      if (!row) return rows;
      const ps = new Set(row.presets);
      if (ps.has(sec)) ps.delete(sec);
      else ps.add(sec);
      next[rowIndex] = { ...row, presets: ps };
      return next;
    });
  };

  const toggleSharedPreset = (sec: number) => {
    setSharedPresets((prev) => {
      const next = new Set(prev);
      if (next.has(sec)) next.delete(sec);
      else next.add(sec);
      return next;
    });
  };

  const onBulkFiles = (list: FileList | null) => {
    const files = list ? Array.from(list) : [];
    setBulkFiles(files);
    setBulkRows(
      files.map((f) => ({
        projectName: f.name.replace(/\.[^.]+$/, '').slice(0, 80) || 'Video',
        extraPrompt: '',
        presets: new Set(sharedPresets),
      }))
    );
  };

  const startJobFromSubmission = async () => {
    if (!submissionIdParam || !selectedVideoId) {
      setError('Choose a video.');
      return;
    }
    const presets = [...selectedPresets].sort((a, b) => a - b);
    if (presets.length === 0) {
      setError('Select at least one duration.');
      return;
    }
    setStarting(true);
    setError(null);
    try {
      const res = await api.post<AiVideoEditJobRow>('/ai-video-edit-jobs', {
        submissionId: submissionIdParam,
        submissionMediaId: selectedVideoId,
        presets,
        extraPrompt: extraPrompt.trim() || null,
      });
      navigate(`/agency/ai-video-edits/new?jobId=${encodeURIComponent(res.data.id)}`, { replace: true });
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to start job';
      setError(typeof msg === 'string' ? msg : 'Failed to start job');
    } finally {
      setStarting(false);
    }
  };

  const startJobFromUpload = async () => {
    if (!uploadFile) {
      setError('Choose a video file.');
      return;
    }
    const presets = [...selectedPresets].sort((a, b) => a - b);
    if (presets.length === 0) {
      setError('Select at least one duration.');
      return;
    }
    setStarting(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('presets', JSON.stringify(presets));
      if (extraPrompt.trim()) fd.append('extraPrompt', extraPrompt.trim());
      if (projectName.trim()) fd.append('projectName', projectName.trim());

      const res = await api.post<AiVideoEditJobRow>('/ai-video-edit-jobs/from-upload', fd);
      navigate(`/agency/ai-video-edits/new?jobId=${encodeURIComponent(res.data.id)}`, { replace: true });
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to start job';
      setError(typeof msg === 'string' ? msg : 'Failed to start job');
    } finally {
      setStarting(false);
    }
  };

  const startBulkUpload = async () => {
    if (bulkFiles.length === 0) {
      setError('Choose one or more videos.');
      return;
    }
    if (sharedPresets.size === 0 && !showOverrides) {
      setError('Select at least one shared duration.');
      return;
    }
    if (showOverrides) {
      if (bulkRows.length !== bulkFiles.length) {
        setError('Row count mismatch.');
        return;
      }
      for (let i = 0; i < bulkRows.length; i++) {
        if (bulkRows[i].presets.size === 0) {
          setError(`Select at least one duration for row ${i + 1}.`);
          return;
        }
      }
    }
    setStarting(true);
    setError(null);
    try {
      const fd = new FormData();
      bulkFiles.forEach((f) => fd.append('files', f));
      fd.append('sharedPresets', JSON.stringify([...sharedPresets].sort((a, b) => a - b)));
      if (sharedDirection.trim()) fd.append('sharedDirection', sharedDirection.trim());
      if (batchLabel.trim()) fd.append('batchLabel', batchLabel.trim());

      // Only send per-file overrides when the user opted into them.
      if (showOverrides) {
        const items = bulkRows.map((r) => ({
          projectName: r.projectName.trim() || 'Video',
          extraPrompt: r.extraPrompt.trim() || null,
          presets: [...r.presets].sort((a, b) => a - b),
        }));
        fd.append('items', JSON.stringify(items));
      }

      const res = await api.post<{
        batchId: string;
        results: { ok: boolean; job?: AiVideoEditJobRow; error?: string }[];
      }>('/ai-video-edit-jobs/from-upload-batch', fd);
      if (res.data.batchId) {
        navigate(`/agency/ai-video-edits/batch/${encodeURIComponent(res.data.batchId)}`, { replace: true });
      } else {
        const err = res.data.results?.find((r) => !r.ok)?.error || 'Batch failed';
        setError(err);
      }
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Batch failed';
      setError(typeof msg === 'string' ? msg : 'Batch failed');
    } finally {
      setStarting(false);
    }
  };

  const sendFollowUp = async () => {
    if (!job?.id || !followUpText.trim()) return;
    setFollowUpSending(true);
    setError(null);
    try {
      await api.post(`/ai-video-edit-jobs/${job.id}/follow-up`, { instruction: followUpText.trim() });
      setFollowUpText('');
      const res = await api.get<AiVideoEditJobRow>(`/ai-video-edit-jobs/${job.id}`);
      setJob(res.data);
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message;
      setError(typeof msg === 'string' ? msg : 'Follow-up failed');
    } finally {
      setFollowUpSending(false);
    }
  };

  const presetControls = (
    <>
      <div>
        <span className="text-sm text-glass-text-2 block mb-2">Ad lengths (seconds)</span>
        <div className="flex flex-wrap gap-3">
          {PRESET_OPTIONS.map((sec) => (
            <label key={sec} className="flex items-center gap-2 text-glass-text cursor-pointer">
              <input
                type="checkbox"
                checked={selectedPresets.has(sec)}
                onChange={() => togglePreset(sec)}
                className="rounded border-glass"
              />
              {sec}s
            </label>
          ))}
        </div>
      </div>
      <div>
        <span className="text-sm text-glass-text-2 block mb-2">Extra direction (optional)</span>
        <Input
          value={extraPrompt}
          onChange={(e) => setExtraPrompt(e.target.value)}
          placeholder="Language, tone, CTA, product name…"
          className="bg-glass-2 border-glass text-glass-text"
        />
      </div>
    </>
  );

  if (jobIdParam && loading && !job && !error) {
    return <div className="py-12 text-glass-text-2">Loading job…</div>;
  }

  if (jobIdParam && error && !job) {
    return (
      <div className="space-y-4 max-w-lg">
        <p className="text-red-400">{error}</p>
        <Link to="/agency/ai-video-edits" className="text-accent2 hover:underline">
          Back to AI Video Edits
        </Link>
      </div>
    );
  }

  if (jobIdParam && job) {
    const outputs = Array.isArray(job.outputs) ? (job.outputs as OutputRow[]) : [];
    const inFlight =
      job.status !== 'COMPLETED' && job.status !== 'FAILED' && job.status !== 'CANCELLED';
    const sourceUrl = job.sourcePreviewUrl;

    return (
      <div className="space-y-6 max-w-4xl">
        <button
          type="button"
          onClick={() => navigate('/agency/ai-video-edits')}
          className="inline-flex items-center gap-2 text-sm text-glass-text-2 hover:text-accent2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to AI Video Edits
        </button>
        <div>
          <h1 className="text-[2rem] font-semibold text-glass-text">
            {job.displayName || 'AI video edit'}
          </h1>
          <p className="text-glass-text-2 mt-1 text-sm">
            Previews use time-limited render links (typically unlisted, not search-indexed). For stricter privacy,
            download files to your own storage before links expire. Need finer control? Open the project in the
            AI editor for manual touch-ups.
          </p>
        </div>

        {job.descriptEditUrl && (
          <Card>
            <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-glass-text font-medium text-sm flex items-center gap-2">
                  <ExternalLink className="w-4 h-4 text-accent2" />
                  Open in AI Editor
                </h3>
                <p className="text-xs text-glass-text-3 mt-1 max-w-xl">
                  Continue with manual fine-tuning in the linked AI editor. Opens in a new tab; you may need to
                  sign in.
                  {inFlight && ' Wait until processing finishes before making manual edits to avoid conflicts.'}
                </p>
              </div>
              <a
                href={job.descriptEditUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-glass inline-flex h-10 items-center justify-center gap-2 rounded-lg px-4 text-sm font-medium border border-glass shrink-0"
              >
                <ExternalLink className="w-4 h-4" />
                Open in AI Editor
              </a>
            </CardContent>
          </Card>
        )}
        <Card>
          <CardHeader>
            <CardTitle className="text-glass-text flex items-center gap-2">
              <Scissors className="w-5 h-5 text-accent2" />
              {job.status}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {inFlight && (
              <p className="text-glass-text-2 text-sm">
                Importing, AI edit, or rendering… This can take several minutes.
              </p>
            )}
            {job.errorMessage && <p className="text-red-400 text-sm">{job.errorMessage}</p>}
            {job.lastAgentAiCreditsUsed != null && (
              <p className="text-xs text-glass-text-3">
                Last run reported ~{job.lastAgentAiCreditsUsed} AI units from the provider; your account is charged
                that many AI edit credits (minimum 1 per successful step). See{' '}
                <Link to="/agency/ai-credits" className="text-accent2 hover:underline">
                  AI credits
                </Link>{' '}
                for balance and history.
              </p>
            )}

            {sourceUrl && (sourceUrl.startsWith('/') || /^https?:\/\//i.test(sourceUrl)) && (
              <div>
                <h3 className="text-glass-text font-medium mb-2 text-sm">Source</h3>
                <div className="rounded-xl border border-glass overflow-hidden max-w-md aspect-video bg-black">
                  <video src={sourceUrl} controls className="w-full h-full object-contain" playsInline />
                </div>
              </div>
            )}

            {job.agentResponse && (
              <div className="rounded-lg border border-glass bg-glass-2 p-4 text-sm text-glass-text-2 whitespace-pre-wrap">
                <span className="text-glass-text font-medium block mb-2">AI summary</span>
                {formatDescriptAgentResponseForDisplay(job.agentResponse)}
              </div>
            )}

            {outputs.length > 0 && (
              <div>
                <h3 className="text-glass-text font-medium mb-3">Rendered clips</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  {outputs.map((o, i) => (
                    <div
                      key={o.job_id || i}
                      className="rounded-xl border border-glass overflow-hidden bg-glass-2"
                    >
                      <p className="text-xs font-medium text-glass-text px-3 py-2 border-b border-glass truncate">
                        {o.label || `Clip ${i + 1}`}
                      </p>
                      {typeof o.download_url === 'string' && (
                        <video
                          src={o.download_url}
                          controls
                          className="w-full aspect-video bg-black object-contain"
                          playsInline
                        />
                      )}
                      <div className="p-3 flex flex-wrap gap-2 text-xs">
                        {typeof o.download_url === 'string' && (
                          <a
                            href={o.download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-accent2 hover:underline"
                          >
                            Download file
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {job.status === 'COMPLETED' && job.descriptProjectId && (
              <div className="rounded-lg border border-glass bg-glass-1 p-4 space-y-3">
                <h3 className="text-glass-text font-medium text-sm">Further adjustment</h3>
                <p className="text-xs text-glass-text-3">
                  One instruction = one extra AI edit on the same project (no chat thread). After it finishes,
                  clips are re-rendered.
                </p>
                <textarea
                  value={followUpText}
                  onChange={(e) => setFollowUpText(e.target.value)}
                  rows={3}
                  placeholder="e.g. Tighter hook in the first 2 seconds on the 6s cut; remove the clinic sign from frame 1…"
                  className="w-full rounded-lg bg-glass-2 border border-glass text-glass-text text-sm px-3 py-2 resize-y"
                />
                <Button onClick={sendFollowUp} disabled={followUpSending || !followUpText.trim()}>
                  {followUpSending ? 'Sending…' : 'Run AI adjustment'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (sourceUpload && bulkUpload && !jobIdParam) {
    return (
      <div className="space-y-6 max-w-4xl">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/agency/ai-video-edits')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-[2rem] font-semibold text-glass-text flex items-center gap-2">
              <Layers className="w-8 h-8 text-accent2" />
              Batch AI video edits
            </h1>
            <p className="text-glass-text-2 text-sm mt-1">
              Drop in several videos, give one set of edit directions, and the system runs every video through the
              same AI edit. Results land on one dashboard. Each successful AI step uses a credit.
            </p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-glass-text">1. Choose videos</CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            <input
              type="file"
              accept="video/mp4,video/webm,video/quicktime"
              multiple
              onChange={(e) => onBulkFiles(e.target.files)}
              className="text-sm text-glass-text file:mr-3 file:rounded-lg file:border-0 file:bg-glass-3 file:px-3 file:py-2 file:text-sm"
            />
            {bulkFiles.length > 0 && (
              <p className="text-xs text-glass-text-3">
                {bulkFiles.length} video{bulkFiles.length === 1 ? '' : 's'} selected.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-glass-text">2. Edit direction (applied to all)</CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div>
              <span className="text-sm text-glass-text-2 block mb-2">Batch name (optional)</span>
              <Input
                value={batchLabel}
                onChange={(e) => setBatchLabel(e.target.value)}
                placeholder="e.g. March promo — all KOL clips"
                className="bg-glass-2 border-glass text-glass-text"
              />
            </div>
            <div>
              <span className="text-sm text-glass-text-2 block mb-2">Ad lengths (seconds)</span>
              <div className="flex flex-wrap gap-3">
                {PRESET_OPTIONS.map((sec) => (
                  <label key={sec} className="flex items-center gap-2 text-glass-text cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sharedPresets.has(sec)}
                      onChange={() => toggleSharedPreset(sec)}
                      className="rounded border-glass"
                    />
                    {sec}s
                  </label>
                ))}
              </div>
            </div>
            <div>
              <span className="text-sm text-glass-text-2 block mb-2">Edit direction</span>
              <textarea
                value={sharedDirection}
                onChange={(e) => setSharedDirection(e.target.value)}
                rows={4}
                placeholder="Describe how every video should be edited: tone, language, hook, CTA, product name, captions style…"
                className="w-full rounded-lg bg-glass-2 border border-glass text-glass-text text-sm px-3 py-2 resize-y"
              />
            </div>

            {bulkFiles.length > 0 && (
              <div className="rounded-lg border border-glass bg-glass-1 p-3">
                <label className="flex items-center gap-2 text-sm text-glass-text cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showOverrides}
                    onChange={(e) => setShowOverrides(e.target.checked)}
                    className="rounded border-glass"
                  />
                  Customise per-video (override name, direction, and lengths)
                </label>
                {showOverrides && (
                  <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-1 mt-3">
                    {bulkFiles.map((f, idx) => (
                      <div
                        key={`${f.name}-${idx}`}
                        className="rounded-lg border border-glass p-3 space-y-2 bg-glass-2"
                      >
                        <p className="text-xs text-glass-text-3 truncate">{f.name}</p>
                        <Input
                          value={bulkRows[idx]?.projectName ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setBulkRows((rows) => {
                              const next = [...rows];
                              if (next[idx]) next[idx] = { ...next[idx], projectName: v };
                              return next;
                            });
                          }}
                          placeholder="Video / project name"
                          className="bg-glass-1 border-glass text-glass-text text-sm"
                        />
                        <Input
                          value={bulkRows[idx]?.extraPrompt ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setBulkRows((rows) => {
                              const next = [...rows];
                              if (next[idx]) next[idx] = { ...next[idx], extraPrompt: v };
                              return next;
                            });
                          }}
                          placeholder="Override direction (blank = use shared direction)"
                          className="bg-glass-1 border-glass text-glass-text text-sm"
                        />
                        <div className="flex flex-wrap gap-2">
                          {PRESET_OPTIONS.map((sec) => (
                            <label key={sec} className="flex items-center gap-1 text-xs text-glass-text">
                              <input
                                type="checkbox"
                                checked={bulkRows[idx]?.presets?.has(sec) ?? false}
                                onChange={() => toggleBulkPreset(idx, sec)}
                                className="rounded border-glass"
                              />
                              {sec}s
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {error && <p className="text-sm text-red-400">{error}</p>}
            <Button onClick={startBulkUpload} disabled={starting || bulkFiles.length === 0}>
              {starting ? 'Starting…' : `Run AI edits on ${bulkFiles.length || 0} video(s)`}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (sourceUpload && !jobIdParam && !bulkUpload) {
    return (
      <div className="space-y-6 max-w-2xl">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/agency/ai-video-edits')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-[2rem] font-semibold text-glass-text flex items-center gap-2">
              <Upload className="w-8 h-8 text-accent2" />
              Upload video
            </h1>
            <p className="text-glass-text-2 text-sm mt-1">
              The API stores the file first (Supabase Storage when configured, otherwise local disk).
              The render service then pulls your file over a signed HTTPS URL.
            </p>
          </div>
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="text-glass-text">Source file</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <input
              type="file"
              accept="video/mp4,video/webm,video/quicktime"
              onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
              className="text-sm text-glass-text file:mr-3 file:rounded-lg file:border-0 file:bg-glass-3 file:px-3 file:py-2 file:text-sm"
            />
            <div>
              <span className="text-sm text-glass-text-2 block mb-2">Video name (shown in list)</span>
              <Input
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="e.g. Client X — March promo"
                className="bg-glass-2 border-glass text-glass-text"
              />
            </div>
            {presetControls}
            {error && <p className="text-sm text-red-400">{error}</p>}
            <Button onClick={startJobFromUpload} disabled={starting || !uploadFile}>
              {starting ? 'Starting…' : 'Upload and start pipeline'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading && submissionIdParam && !jobIdParam) {
    return <div className="py-12 text-glass-text-2">Loading…</div>;
  }

  if (!submissionIdParam && !jobIdParam && !sourceUpload) {
    return (
      <div className="space-y-4 max-w-lg">
        <p className="text-glass-text-2">Start from a submission or upload.</p>
        <div className="flex flex-col gap-2">
          <Link to="/agency/submissions" className="text-accent2 hover:underline">
            Go to Submissions
          </Link>
          <Link to="/agency/ai-video-edits/new?source=upload" className="text-accent2 hover:underline">
            Upload video
          </Link>
          <Link to="/agency/ai-video-edits/new?source=upload&bulk=1" className="text-accent2 hover:underline">
            Bulk upload
          </Link>
        </div>
      </div>
    );
  }

  if (!submission) {
    return <div className="py-12 text-red-400">{error || 'Submission not found'}</div>;
  }

  const slug = toSlug(submission.invitation?.job?.title || 'submission', submission.id);

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/agency/submissions/${slug}`)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-[2rem] font-semibold text-glass-text">New AI video edit</h1>
          <p className="text-glass-text-2 text-sm mt-1">
            Processing runs on the platform; each successful AI step deducts credits from your agency balance.
          </p>
        </div>
      </div>

      {videoOptions.length === 0 ? (
        <p className="text-glass-text-2">This submission has no video files. Add a video first.</p>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-glass-text">Source video</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <select
              value={selectedVideoId}
              onChange={(e) => {
                const v = e.target.value;
                navigate(
                  `/agency/ai-video-edits/new?submissionId=${encodeURIComponent(submission.id)}&mediaId=${encodeURIComponent(v)}`,
                  { replace: true }
                );
              }}
              className="w-full rounded-lg bg-glass-2 border border-glass text-glass-text px-3 py-2 text-sm"
            >
              {videoOptions.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.title || 'Video'} · {m.id.slice(0, 8)}…
                </option>
              ))}
            </select>

            {presetControls}

            {error && <p className="text-sm text-red-400">{error}</p>}

            <Button onClick={startJobFromSubmission} disabled={starting || !selectedVideoId}>
              {starting ? 'Starting…' : 'Start pipeline'}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
